import { useState } from 'react';
import { Button, CircularProgress, Dialog, DialogActions, makeStyles } from '@material-ui/core';

import { User } from '../../../users/types/user.type';
import { UserSearchTable } from '../../../users/components/Tables/UserSearchTable.component';
import pluralFormatter from '../../../shared/utils/plural-formater';
import { useUpdateOrganizationMembers } from '../../hooks/update-organization-members.hook';

type Props = {
  actionLabel: string;
  organizationId: string;
  refreshOrganization: () => Promise<void>;
};

const useStyles = makeStyles((theme) => ({
  root: {
    '& >.MuiDialog-container': {
      '& > .MuiPaper-root': {
        minWidth: '65svw',
        padding: '1rem 0.75rem',
      },
    },
  },
  content: {
    padding: '0.5rem',
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',

    '& > p': {
      margin: 0,
    },

    '& > section': {
      display: 'flex',
      gap: '0.5rem',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
  },
  ctaButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export default function AddMembersDialog({ actionLabel, organizationId, refreshOrganization }: Props) {
  const classes = useStyles();

  const [open, setOpen] = useState(false);
  const [membersToAdd, setMembersToAdd] = useState<User[]>([]);

  const { updateOrganizationMembers, isUpdating: isUpdatingMembers } = useUpdateOrganizationMembers();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleAddMember = (user: User) => {
    setMembersToAdd((prev) => (prev.find((member) => member.id === user.id) ? prev : [...prev, user]));
  };

  const handleCancel = () => {
    setMembersToAdd([]);
    handleClose();
  };

  const handleSubmit = async () => {
    const updatedOrganization = await updateOrganizationMembers({
      organizationId,
      members: {
        add: membersToAdd.map((user) => user.id),
      },
    });

    setMembersToAdd([]);
    handleClose();

    if (updatedOrganization) await refreshOrganization();
  };

  const selectedMemberIds = membersToAdd.map((member) => member.id);

  return (
    <>
      <Button size="small" variant="contained" className={classes.ctaButton} onClick={handleOpen}>
        {actionLabel}
      </Button>
      <Dialog open={open} onClose={handleClose} className={classes.root}>
        <section className={classes.content}>
          <UserSearchTable
            selectedMemberIds={selectedMemberIds}
            onAddMember={handleAddMember}
            maxHeight="40svh"
          />
        </section>
        <DialogActions className={classes.actions}>
          <p>
            {membersToAdd.length > 0
              ? `${membersToAdd.length} ${pluralFormatter({ count: membersToAdd.length, singular: 'utilisateur sélectionné', plural: 'utilisateurs sélectionnés' })}`
              : 'Aucun utilisateur sélectionné'}
          </p>
          <section>
            <Button onClick={handleCancel} disabled={isUpdatingMembers}>
              Annuler
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={!membersToAdd.length || isUpdatingMembers}
            >
              {isUpdatingMembers ? <CircularProgress size={16} /> : 'Ajouter'}
            </Button>
          </section>
        </DialogActions>
      </Dialog>
    </>
  );
}
